import { createTheme, PaletteOptions } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface Palette {
    lower?: Palette["primary"];
  }
  interface PaletteOptions {
    lower?: PaletteOptions["primary"];
  }
}

const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#3A2ADA",
      light: "#574ad4",
      dark: "#2718BF",
      contrastText: "#fff",
    },
    secondary: {
      main: "#dc004e",
      light: "#ff5c8d",
      dark: "#9a0036",
      contrastText: "#fff",
    },
    success: {
      main: "#279C8A",
    },
    error: {
      main: "#800006",
    },
    warning: {
      main: "#A7000F",  
    },
    info: {
      main: "#E85800",
    },
    lower: {
      main: "#F2BF00",
    },
    background: {
      default: "#fbfbff",
      paper: "#ffffff",
    },
    text: {
      primary: "#1E2036",
      secondary: "#696595",
    },
    divider: "rgba(0, 0, 0, 0.12)",
  },
  typography: {
    fontFamily: "Manrope, sans-serif",
    fontWeightBold: 700,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#fbfbff",
          color: "#1E2036",
        },
        ".formik-error": {
          color: "#f44336",
          fontSize: "13px",
          fontWeight: "500",
        },
        // AG Grid Light Theme Styles
        ".ag-theme-material": {
          "--ag-background-color": "#ffffff",
          "--ag-foreground-color": "#1E2036",
          "--ag-header-background-color": "#E7E6F3",
          "--ag-odd-row-background-color": "#ffffff",
          "--ag-even-row-background-color": "#ffffff",
          "--ag-header-foreground-color": "#1E2036",
          "--ag-data-color": "#1E2036",
          "--ag-row-hover-color": "rgba(25, 118, 210, 0.04)",
          "--ag-selected-row-background-color": "rgba(25, 118, 210, 0.08)",
          "--ag-border-color": "rgba(0, 0, 0, 0.12)",
          "--ag-secondary-border-color": "rgba(0, 0, 0, 0.12)",
          "--ag-alpine-active-color": "#3A2ADA",
          "--ag-range-selection-border-color": "#3A2ADA",
          "--ag-input-border-color": "rgba(0, 0, 0, 0.23)",
          "--ag-input-focus-border-color": "#3A2ADA",
          "--ag-chip-background-color": "rgba(0, 0, 0, 0.08)",
          "--ag-row-border-color": "rgba(0, 0, 0, 0.12)",
          "--ag-cell-horizontal-border": "rgba(0, 0, 0, 0.12)",
          "& .ag-root-wrapper": {
            border: "none",
            backgroundColor: "#ffffff",
          },
          "& .ag-row": {
            borderColor: "rgba(0, 0, 0, 0.12)",
            backgroundColor: "#ffffff",
            "&.ag-row-odd": {
              backgroundColor: "#ffffff",
            },
            "&.ag-row-even": {
              backgroundColor: "#ffffff",
            },
          },
          "& .ag-cell": {
            borderColor: "rgba(0, 0, 0, 0.12)",
            backgroundColor: "#ffffff",
            color: "#1E2036",
          },
          "& .ag-paging-panel": {
            color: "#1E2036",
            borderTop: "1px solid rgba(0, 0, 0, 0.12)",
            backgroundColor: "#ffffff",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          color: "#1E2036",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          "& .container": {
            backgroundColor: "#ffffff",
            color: "#1E2036",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            "& .header": {
              color: "#1E2036",
              "& h2": {
                color: "#1E2036",
              },
            },
            "& .list_data_container": {
              backgroundColor: "#ffffff",
              color: "#1E2036",
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          backgroundImage: "none",
          color: "#1E2036",
          border: "1px solid rgba(0, 0, 0, 0.12)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "#3A2ADA",
            color: "#ffffff",
          },
          "&:disabled": {
            backgroundColor: "#3A2ADA",
            color: "#ffffff",
            opacity: "0.5",
          },
          borderRadius: "6px",
          textTransform: "none",
          fontSize: "12px",
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: "#574ad4",
          },
        },
        containedSecondary: {
          "&:hover": {
            backgroundColor: "#9A0036",
          },
        },
      },
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#1E2036",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            "&:not(.MuiInputBase-multiline)": {
              height: "40px",
              minHeight: "40px",
            },
          },
          "& .MuiInputLabel-root": {
            fontSize: "12px",
            color: "#696595",
            top: "-4px",
          },
          "& .MuiInputBase-input": {
            fontSize: "14px",
            fontWeight: "500",
            color: "#1E2036",
            padding: "10px 14px",
            height: "20px",
            boxSizing: "border-box",
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: "6px",
            minHeight: "40px",
            "& fieldset": {
              borderColor: "rgba(0, 0, 0, 0.23)",
            },
            "&:hover fieldset": {
              borderColor: "#3A2ADA",
            },
            "&.Mui-focused fieldset": {
              borderWidth: "2px",
            },
          },
          "& .MuiFormHelperText-root": {
            marginLeft: "0px",
            fontSize: "12px",
            fontWeight: "500",
          },
          "& .MuiFilledInput-root": {
            backgroundColor: "#F4F6F8",
            minHeight: "40px",
          },
          "& .MuiFilledInput-root:hover": {
            backgroundColor: "#F4F6F8",
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: "#1E2036",
          "&.error": {
            color: "#f44336",
            fontSize: "13px",
          },
        },
      },
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#3A2ADA",
      light: "#574ad4",
      dark: "#2718BF",
      contrastText: "#fff",
    },
    secondary: {
      main: "#dc004e",
      light: "#ff5c8d",
      dark: "#9a0036",
      contrastText: "#fff",
    },
    success: {
      main: "#279C8A",
    },
    error: {
      main: "#800006",
    },
    warning: {
      main: "#A7000F",
    },
    info: {
      main: "#E85800",
    },
    lower: {
      main: "#F2BF00",
    },
    background: {
      default: "#0A0A0A",
      paper: "#1E1E1E",
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#B3B3B3",
    },
    divider: "rgba(255, 255, 255, 0.12)",
  },
  typography: {
    fontFamily: "Manrope, sans-serif",
    fontWeightBold: 700,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#0A0A0A",
          color: "#FFFFFF",
        },
        ".formik-error": {
          color: "#f44336",
          fontSize: "13px",
          fontWeight: "500",
        },
        // AG Grid Dark Theme Styles
        ".ag-theme-material": {
          "--ag-background-color": "#1E1E1E",
          "--ag-foreground-color": "#FFFFFF",
          "--ag-header-background-color": "#2D2D2D",
          "--ag-odd-row-background-color": "#1E1E1E",
          "--ag-even-row-background-color": "#2A2A2A",
          "--ag-header-foreground-color": "#FFFFFF",
          "--ag-data-color": "#FFFFFF",
          "--ag-row-hover-color": "rgba(58, 42, 218, 0.1)",
          "--ag-selected-row-background-color": "rgba(58, 42, 218, 0.2)",
          "--ag-border-color": "rgba(255, 255, 255, 0.12)",
          "--ag-secondary-border-color": "rgba(255, 255, 255, 0.12)",
          "--ag-alpine-active-color": "#3A2ADA",
          "--ag-range-selection-border-color": "#3A2ADA",
          "--ag-input-border-color": "rgba(255, 255, 255, 0.23)",
          "--ag-input-focus-border-color": "#3A2ADA",
          "--ag-chip-background-color": "rgba(255, 255, 255, 0.08)",
          "--ag-row-border-color": "rgba(255, 255, 255, 0.12)",
          "--ag-cell-horizontal-border": "rgba(255, 255, 255, 0.12)",
          "& .ag-root-wrapper": {
            border: "none",
            backgroundColor: "#1E1E1E",
          },
          "& .ag-row": {
            borderColor: "rgba(255, 255, 255, 0.12)",
            backgroundColor: "#1E1E1E",
            "&.ag-row-odd": {
              backgroundColor: "#1E1E1E",
            },
            "&.ag-row-even": {
              backgroundColor: "#2A2A2A",
            },
          },
          "& .ag-cell": {
            borderColor: "rgba(255, 255, 255, 0.12)",
            backgroundColor: "transparent",
            color: "#FFFFFF",
          },
          "& .ag-paging-panel": {
            color: "#FFFFFF",
            borderTop: "1px solid rgba(255, 255, 255, 0.12)",
            backgroundColor: "#1E1E1E",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#1E1E1E",
          color: "#FFFFFF",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.3)",
          "& .container": {
            backgroundColor: "#1E1E1E",
            color: "#FFFFFF",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            "& .header": {
              color: "#FFFFFF",
              "& h2": {
                color: "#FFFFFF",
              },
            },
            "& .list_data_container": {
              backgroundColor: "#1E1E1E",
              color: "#FFFFFF",
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#1E1E1E",
          backgroundImage: "none",
          color: "#FFFFFF",
          border: "1px solid rgba(255, 255, 255, 0.12)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "#3A2ADA",
            color: "#ffffff",
          },
          "&:disabled": {
            backgroundColor: "#3A2ADA",
            color: "#ffffff",
            opacity: "0.5",
          },
          borderRadius: "6px",
          textTransform: "none",
          fontSize: "12px",
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: "#574ad4",
          },
        },
        containedSecondary: {
          "&:hover": {
            backgroundColor: "#9A0036",
          },
        },
      },
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#FFFFFF",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            "&:not(.MuiInputBase-multiline)": {
              height: "40px",
              minHeight: "40px",
            },
          },
          "& .MuiInputLabel-root": {
            fontSize: "12px",
            color: "#B3B3B3",
            top: "-4px",
          },
          "& .MuiInputBase-input": {
            fontSize: "14px",
            fontWeight: "500",
            color: "#FFFFFF",
            padding: "10px 14px",
            height: "20px",
            boxSizing: "border-box",
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: "6px",
            minHeight: "40px",
            "& fieldset": {
              borderColor: "rgba(255, 255, 255, 0.23)",
            },
            "&:hover fieldset": {
              borderColor: "#3A2ADA",
            },
            "&.Mui-focused fieldset": {
              borderWidth: "2px",
            },
          },
          "& .MuiFormHelperText-root": {
            marginLeft: "0px",
            fontSize: "12px",
            fontWeight: "500",
          },
          "& .MuiFilledInput-root": {
            backgroundColor: "#2D2D2D",
            minHeight: "40px",
          },
          "& .MuiFilledInput-root:hover": {
            backgroundColor: "#2D2D2D",
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: "#FFFFFF",
          "&.error": {
            color: "#f44336",
            fontSize: "13px",
          },
        },
      },
    },
  },
});

export { lightTheme, darkTheme }; 